import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Particles, { ParticlesProvider, useParticlesProvider } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { ArrowRight, CalendarCheck, ShieldCheck, Award, BadgeCheck } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const initEngine = async (engine) => {
  await loadSlim(engine);
};

function NetworkCanvas({ options }) {
  const { loaded } = useParticlesProvider();
  if (!loaded) return null;
  return <Particles id="hero-particles" options={options} className="absolute inset-0" />;
}

function HeroContent() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yOrbit = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const options = useMemo(
    () => ({
      fullScreen: false,
      fpsLimit: 60,
      detectRetina: true,
      particles: {
        number: { value: 55, density: { enable: true, area: 900 } },
        color: { value: ["#00A8E8", "#FF9500", "#FFFFFF"] },
        links: { enable: true, distance: 150, color: "#00A8E8", opacity: 0.28, width: 1 },
        move: { enable: true, speed: 0.55, outModes: { default: "out" } },
        opacity: { value: { min: 0.15, max: 0.6 } },
        size: { value: { min: 0.8, max: 2.4 } },
        shape: { type: "circle" },
      },
      interactivity: {
        events: { onHover: { enable: true, mode: "grab" } },
        modes: { grab: { distance: 170, links: { opacity: 0.5 } } },
      },
    }),
    []
  );

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-nexblue">
      <div className="absolute inset-0" aria-hidden="true">
        <ParticlesProvider init={initEngine}>
          <NetworkCanvas options={options} />
        </ParticlesProvider>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,168,232,0.14),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,149,0,0.08),transparent_50%)]" />
      </div>

      <motion.div style={{ y: yOrbit }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="orbit-ring w-[640px] h-[640px] -right-40 -top-40 animate-spin-slow" />
        <div className="orbit-ring w-[900px] h-[900px] -right-64 -top-64" style={{ borderColor: "rgba(255,149,0,0.10)" }} />
        <div className="orbit-ring w-[420px] h-[420px] -left-32 bottom-[-120px]" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
      </motion.div>

      <motion.div style={{ y: yContent, opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-16 sm:pt-32 sm:pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-nexorange" />
          <span className="text-xs sm:text-sm font-semibold tracking-[0.35em] text-nexcyan" data-testid="hero-tagline">
            INNOVATE&nbsp;|&nbsp;EMPOWER&nbsp;|&nbsp;ELEVATE
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tighter text-white text-[1.9rem] leading-[1.12] sm:text-6xl lg:text-7xl sm:leading-[1.04] max-w-4xl">
          <span className="block overflow-hidden pb-1">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.25, ease: EASE }}>
              Turning Real Business Problems
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.4, ease: EASE }}>
              into <span className="text-nexcyan">Practical Technology</span> <span className="text-nexorange">Solutions.</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="mt-5 sm:mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
          data-testid="hero-subheading"
        >
          We help businesses build and modernize technology through cloud management, AI and automation, and custom software engineering. We also build products designed to solve real-world challenges.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          className="mt-8 sm:mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#contact")}
            data-testid="hero-get-started-btn"
            className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-nexorange px-6 sm:px-7 py-3.5 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.05] hover:shadow-[0_12px_32px_rgba(255,149,0,0.4)]"
          >
            Discuss a Business Requirement
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("#products")}
            data-testid="hero-explore-products-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors duration-300 hover:border-nexcyan hover:text-nexcyan"
          >
            Explore Our Products
          </button>
          <button
            onClick={() => scrollToId("#contact")}
            data-testid="hero-schedule-demo-btn"
            className="inline-flex items-center gap-2 px-3 py-3.5 text-sm font-semibold text-white/70 transition-colors duration-300 hover:text-nexcyan"
          >
            <CalendarCheck size={17} />
            Schedule Demo
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-10 sm:mt-14 flex flex-wrap gap-3"
          data-testid="hero-trust-badges"
        >
          {[
            { icon: Award, label: "Microsoft Veteran Leadership" },
            { icon: BadgeCheck, label: "AWS Certified Expertise" },
            { icon: ShieldCheck, label: "SOC 2 Compliant" },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="chip-glow inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-medium text-white/80 backdrop-blur-md"
            >
              <Icon size={14} className="text-nexcyan" />
              {label}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        aria-hidden="true"
      >
        <div className="w-[26px] h-[42px] rounded-full border border-white/30 flex justify-center pt-2">
          <motion.div
            className="w-1 h-2 rounded-full bg-nexcyan"
            animate={{ y: [0, 14, 0], opacity: [1, 0.2, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
export default HeroContent;
